import { useEffect, useRef, useState, useCallback } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import FilterInspeksi from "./Filter";
import api, { API_BASE_URL } from '../auth/apiClient';
import {
  Box,
  Flex,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ButtonGroup,
  Heading,
  ModalBody,
  Image,
  ModalCloseButton,
  Text,
  Grid,
  HStack,
  VStack,
  useBreakpointValue
} from "@chakra-ui/react";
import { FiPlus, FiMinus, } from "react-icons/fi";
import { TbLine } from "react-icons/tb";
import { MdClose, MdMyLocation, MdOutlineLegendToggle, MdOutlineKeyboardArrowRight, MdOutlineKeyboardArrowLeft } from "react-icons/md";
import { getCategoryColor } from '../utils/CategoryColors';
import * as turf from '@turf/turf';
import ClusterExploder from "./ClusterExplode";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { trackDownload } from "../activity";

interface FilterState {
  tanggal?: string;
  start_date?: string;
  end_date?: string;
  estate?: string;
  divisi?: string;
  category?: number;
  penalty?: number;
  pemanen?: string;
  users?: string;
  company_id?: number;
  q?: string;
}
//FF4500
export const _01_RED_DARK = "#6B0000" // Merah oranye (sangat rendah)
export const _02_RED = "#FF0000" // Merah terang (gagal total)
export const _03_ORANGE_DARK = "#FF8C00" // Oranye gelap (rendah)
export const _04_ORANGE = "#FFA500" // Oranye (cukup rendah)
export const _05_GOLD = "#FFD700" // Emas (cukup baik)
export const _06_GREEN_YELLOW = "#ADFF2F" // Hijau kekuningan (baik)
export const _07_GREEN_LIGHT = "#02C902" // Hijau terang (sangat baik)
export const _08_GREEN_DARK = "#008000" // Hijau tua (target tercapai)

export const _01_TRANS_RED = "#FFA0A0"
export const _02_TRANS_YELLOW = "#FFDD55"
export const _03_TRANS_BLUE = "#D0D0FF"

export const _PANEN = "#E74C3C"           // Merah terang
export const _MUTU_ANCAK = "#E67E22"      // Oranye tua terang
export const _MUTU_BUAH = "#1ABC9C"       // Toska cerah
export const _RAWAT = "#A2CC2E"           // Hijau segar
export const _PUPUK = "#FF8C00"           // Oranye mencolok
export const _PATROLI = "#9B59B6"         // Ungu tua
export const _TRAKS = "#3498DB"           // Biru terang
export const _INFRA = "#00BCD4"           // Biru toska
export const _HPT = "#FFD700"             // Emas
// export const _MUTU_ANCAK = "#E67E22"      // Oranye tua terang
export const _PMKS = "#C0392B"            // Marun
export const _ADMIN = "#7F8C8D"           // Abu-abu modern

const MapInspectionComponent = () => {
  const [selectedInspeksi, setSelectedInspeksi] = useState<{
    uuid: string;
    id: number;
    estate: string;
    divisi: string;
    category?: string;
    user: string;
    penalty_code?: string;
    penalty_description?: string;
    pemanen?: string;
    emp_pemanen?: string;
    pemanen_nomor_induk_pegawai?: string;
    pemanen_kemandoran?: string;
    emp_pemanen_nomor_induk_pegawai?: string;
    emp_pemanen_kemandoran?: string;
    no_tph?: string;
    no_baris?: string;
    no_pokok?: string;
    qty?: number;
    ripe_qty?: number;
    unripe_qty?: number;
    rotten_qty?: number;
    lstalk_qty?: number;
    loose_qty?: number;
    tph?: string;
    abnormal_01_qty?: number;
    abnormal_02_qty?: number;
    abnormal_03_qty?: number;
    abnormal_04_qty?: number;
    no_potong?: string;
    nomor_induk_pegawai: string;
    job_position: string;
    blok: string;
    date: string;
    time: string;
    description: string;
    pictures?: string[];
  } | null>(null);


  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<maplibregl.Map | null>(null);
  const [filters, setFilters] = useState<FilterState>({});
  const [pendingApply, setPendingApply] = useState(false);
  const [isSatellite, setIsSatellite] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showConnectionLines, setShowConnectionLines] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(false);

  const exportPDF = async () => {
    const input = document.getElementById("modal-detail-inspeksi");
    if (input) {
      const canvas = await html2canvas(input);
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      // pdf.addImage(imgData, "PNG", 10, 10);
      pdf.save(`Inspeksi-${selectedInspeksi?.id}.pdf`);
      trackDownload("PDF Detail Inspeksi", { inspeksi_id: selectedInspeksi?.id });
    }
  };

  const shareViaWhatsApp = () => {
    const text = `📋 *Detail Inspeksi #${selectedInspeksi?.id}*\nEstate: ${selectedInspeksi?.estate}\nDivisi: ${selectedInspeksi?.divisi}\nTanggal: ${selectedInspeksi?.date} ${selectedInspeksi?.time}\nPemanen: ${selectedInspeksi?.pemanen}\nDeskripsi: ${selectedInspeksi?.description}`;
    const encoded = encodeURIComponent(text);
    window.open(`https://wa.me/?text=${encoded}`, "_blank");
  };

  const shareViaEmail = () => {
    const subject = `Detail Inspeksi #${selectedInspeksi?.id}`;
    const body = `Berikut detail inspeksi:\n\nEstate: ${selectedInspeksi?.estate}\nDivisi: ${selectedInspeksi?.divisi}\nTanggal: ${selectedInspeksi?.date} ${selectedInspeksi?.time}\nPemanen: ${selectedInspeksi?.pemanen}\n\nDeskripsi:\n${selectedInspeksi?.description}`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const defaultSidebarState = useBreakpointValue({ base: false, md: true });

  useEffect(() => {
    setSidebarOpen(defaultSidebarState ?? true);
  }, [defaultSidebarState]);

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);


  const [inspeksiData, setInspeksiData] = useState<GeoJSON.FeatureCollection<GeoJSON.Point, any>>({
    type: "FeatureCollection",
    features: [],
  });


  const clearFilters = useCallback(() => {
    setFilters({});
    setPendingApply(true);
  }, []);

  useEffect(() => {
    if (!mapContainerRef.current) return;
    const mapInstance = new maplibregl.Map({
      container: mapContainerRef.current,
      style: "https://api.maptiler.com/maps/streets-v2/style.json?key=Q3fXi2sghTMg1wQSFgxK",
      center: [104.083064, -3.125992],
      zoom: 10.9,
      attributionControl: false,
    });
    // const mapInstance = new maplibregl.Map({
    //   container: mapContainerRef.current,
    //   style: "https://api.maptiler.com/maps/streets-v2/style.json?key=QY0dAspaeAOotq0bMeLo", // Ganti tiles.json dengan style.json
    //   center: [104.211177, -3.141669],
    //   zoom: 11.5,
    //   attributionControl: false,
    // });

    mapInstance.on("load", async () => {
      await loadGeoJSON(mapInstance);
      await fetchInspeksiData(mapInstance);

      // layer satelit dan set visibility "none" secara default
      mapInstance.addSource("satellite", {
        type: "raster",
        tiles: [
          "https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=QY0dAspaeAOotq0bMeLo",
        ],
        tileSize: 256,
      });
      mapInstance.addLayer({
        id: "satellite-layer",
        type: "raster",
        source: "satellite",
        layout: { visibility: "none" },
      });
      // Pindahkan layer satelit ke bawah layer label agar nama blok tetap terlihat
      mapInstance.moveLayer("satellite-layer", "geojson-layer");
    });

    setMap(mapInstance);

    return () => {
      if (mapInstance) {
        // Remove all layers and sources
        if (mapInstance.getLayer('connection-lines')) {
          mapInstance.removeLayer('connection-lines');
        }
        if (mapInstance.getSource('connection-lines')) {
          mapInstance.removeSource('connection-lines');
        }
        if (mapInstance.getLayer('inspeksi-marker')) {
          mapInstance.removeLayer('inspeksi-marker');
        }
        if (mapInstance.getSource('inspeksi-marker')) {
          mapInstance.removeSource('inspeksi-marker');
        }
        if (mapInstance.getLayer('satellite-layer')) {
          mapInstance.removeLayer('satellite-layer');
        }
        if (mapInstance.getSource('satellite-layer')) {
          mapInstance.removeSource('satellite-layer');
        }
        if (mapInstance.getLayer('lkk-label')) {
          mapInstance.removeLayer('lkk-label');
        }
        if (mapInstance.getLayer('lkk-layer')) {
          mapInstance.removeLayer('lkk-layer');
        }
        if (mapInstance.getSource('lkk-source')) {
          mapInstance.removeSource('lkk-source');
        }
        if (mapInstance.getLayer('geojson-label')) {
          mapInstance.removeLayer('geojson-label');
        }
        if (mapInstance.getLayer('geojson-layer')) {
          mapInstance.removeLayer('geojson-layer');
        }
        if (mapInstance.getSource('geojson-source')) {
          mapInstance.removeSource('geojson-source');
        }

        // Remove the map instance
        mapInstance.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (!map) return;
    if (!pendingApply) return;

    // jalankan data-fetch dengan filters terbaru
    fetchInspeksiData(map, filters)
      .catch(console.error)
      .finally(() => {
        setPendingApply(false); // reset flag
      });
  }, [map, pendingApply, filters]);


  const getBlockColor = (latestDate?: Date): string => {
    // Jika tidak ada tanggal, return merah
    if (!latestDate) {
      return _01_TRANS_RED; // red
    }

    // Dapatkan tanggal hari ini untuk perbandingan
    const today = new Date();

    // Hitung selisih bulan antara hari ini dan inspeksi terakhir
    const diffMonths =
      (today.getFullYear() - latestDate.getFullYear()) * 12 +
      (today.getMonth() - latestDate.getMonth());


    // Warna berdasarkan keterbaruan inspeksi
    if (diffMonths === 0) {
      // Bulan ini → biru
      return _03_TRANS_BLUE;
    } else if (diffMonths === 1) {
      // Bulan lalu → kuning
      return _02_TRANS_YELLOW;
    } else {
      // Lebih dari 1 bulan → merah
      return _01_TRANS_RED;
    }
  };


  const loadGeoJSON = async (mapInstance: maplibregl.Map) => {
    try {
      // Load batas blok kebun GBS
      const response = await fetch("/data/batas_blok_kebun_gbs.geojson");
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const geojsonData = await response.json();

      // Set warna default
      geojsonData.features.forEach((feature: any) => {
        feature.properties.color = '#ffffff';
      });

      if (!mapInstance.getSource("geojson-source")) {
        mapInstance.addSource("geojson-source", {
          type: "geojson",
          data: geojsonData
        });
        mapInstance.addLayer({
          id: "geojson-layer",
          type: "fill",
          source: "geojson-source",
          paint: {
            "fill-color": ["get", "color"], // Ambil warna dari property feature
            "fill-opacity": 1,
            "fill-outline-color": "#000000",
          },
        });
        mapInstance.addLayer({
          id: "geojson-label",
          type: "symbol",
          source: "geojson-source",
          layout: {
            "text-field": ["get", "LandUse_26"],
            "text-size": 14,
            "text-anchor": "center",
            "text-offset": [0, 0],
          },
          paint: {
            "text-color": "#000000",
            "text-halo-width": 2,
          },
        });
      } else {
        (mapInstance.getSource("geojson-source") as maplibregl.GeoJSONSource).setData(geojsonData);
      }

      // Load Blok Planted LKK
      const lkkResponse = await fetch("/data/Blok_Planted_LKK.geojson");
      if (!lkkResponse.ok) {
        throw new Error(`HTTP error! Status: ${lkkResponse.status}`);
      }

      const lkkData = await lkkResponse.json();

      // Set warna default untuk LKK blocks
      lkkData.features.forEach((feature: any) => {
        feature.properties.color = '#ffffff';
      });

      if (!mapInstance.getSource("lkk-source")) {
        mapInstance.addSource("lkk-source", {
          type: "geojson",
          data: lkkData
        });
        mapInstance.addLayer({
          id: "lkk-layer",
          type: "fill",
          source: "lkk-source",
          paint: {
            "fill-color": ["get", "color"],
            "fill-opacity": 1,
            "fill-outline-color": "#000000",
          },
        });
        mapInstance.addLayer({
          id: "lkk-label",
          type: "symbol",
          source: "lkk-source",
          layout: {
            "text-field": ["get", "blok_code"],
            "text-size": 14,
            "text-anchor": "center",
            "text-offset": [0, 0],
          },
          paint: {
            "text-color": "#000000",
            "text-halo-width": 2,
          },
        });
      } else {
        (mapInstance.getSource("lkk-source") as maplibregl.GeoJSONSource).setData(lkkData);
      }

      // Load Batas MRN
      const mrnResponse = await fetch("/data/mrn_map.geojson");
      if (mrnResponse.ok) {
        const mrnData = await mrnResponse.json();
        mrnData.features.forEach((feature: any) => {
          feature.properties.color = '#ffffff';
        });

        if (!mapInstance.getSource("mrn-source")) {
          mapInstance.addSource("mrn-source", {
            type: "geojson",
            data: mrnData
          });
          mapInstance.addLayer({
            id: "mrn-layer",
            type: "fill",
            source: "mrn-source",
            paint: {
              "fill-color": ["get", "color"],
              "fill-opacity": 0.6,
              "fill-outline-color": "#000000",
            },
          });
          mapInstance.addLayer({
            id: "mrn-label",
            type: "symbol",
            source: "mrn-source",
            layout: {
              "text-field": ["coalesce", ["get", "blok_code"], ["get", "LandUse_26"], ""],
              "text-size": 14,
              "text-anchor": "center",
              "text-offset": [0, 0],
            },
            paint: {
              "text-color": "#000000",
              "text-halo-width": 2,
            },
          });
        } else {
          (mapInstance.getSource("mrn-source") as maplibregl.GeoJSONSource).setData(mrnData);
        }
      }
    } catch (error) {
      console.error("Error loading GeoJSON file:", error);
    }
  };

  const fetchInspeksiData = async (mapInstance: maplibregl.Map, filterParams?: FilterState) => {
    try {
      if (mapInstance.getLayer("inspeksi-marker")) {
        mapInstance.removeLayer("inspeksi-marker");
      }
      if (mapInstance.getSource("inspeksi-source")) {
        mapInstance.removeSource("inspeksi-source");
      }

      // Get latest inspection dates for block coloring
      const latestInspections = await api.get('/inspeksi/latest-blok-inspeksi', {
        params: {
          category_id: filterParams?.category || undefined
        }
      });
      console.log("Latest inspection dates per block:", latestInspections.data);

      // Fetch filtered data for markers
      const filtersToUse = filterParams || filters;
      const params = Object.entries(filtersToUse).reduce((acc, [key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          (typeof value !== "string" || value.trim() !== "")
        ) {
          // Skip estate_id as it's only used internally
          if (key === 'estate_id') return acc;

          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, string | number>);

      console.log("Fetching data with params:", params);

      // Remove old marker layer & source (clean slate)
      if (mapInstance.getLayer("inspeksi-marker")) {
        mapInstance.removeLayer("inspeksi-marker");
      }
      if (mapInstance.getSource("inspeksi-source")) {
        mapInstance.removeSource("inspeksi-source");
      }

      // Fetch filtered data for markers
      const response = await api.get('/inspeksi/all', { params });
      if (response.status !== 200) throw new Error(`HTTP error! Status: ${response.status}`);
      const inspections: any[] = response.data;
      console.log("Data Inspeksi Loaded:", inspections);

      // Update warna blok di geojson-source berdasarkan inspeksi terbaru
      const geoSource = mapInstance.getSource("geojson-source") as maplibregl.GeoJSONSource;
      if (geoSource) {
        const geoData = (await geoSource.getData()) as GeoJSON.FeatureCollection;

        // Counter for coloring statistics
        let colorStats = {
          green: 0,
          yellow: 0,
          red: 0
        };

        // Process each feature (block) in the GeoJSON data
        geoData.features.forEach((feature: any) => {
          // Get the block name that's actually displayed on the map
          const namaBlok = feature.properties?.LandUse_26;

          if (namaBlok) {
            // Try exact match first
            let lastDate = latestInspections.data.find((item: any) => item.blok === namaBlok)?.latest_inspection;
            console.log("Latest inspection date for block:", namaBlok, lastDate);

            // If no exact match, try case-insensitive matching
            if (!lastDate) {
              const matchingBlock = latestInspections.data.find(
                (item: any) => item.blok.toLowerCase() === namaBlok.toLowerCase()
              );
              if (matchingBlock) {
                lastDate = matchingBlock.latest_inspection;
              }
            }

            // Apply color based on inspection date
            const color = getBlockColor(lastDate ? new Date(lastDate) : undefined);
            feature.properties.color = color;

            // Update statistics
            if (color === "#2ecc71") colorStats.green++;
            else if (color === "#fcd73f") colorStats.yellow++;
            else if (color === "#e74c3c") colorStats.red++;

            // Log info for some blocks for debugging
            if (Math.random() < 0.05) { // Log ~5% of blocks to avoid console spam
              if (lastDate) {
                console.log(`Block ${namaBlok}: Last inspection ${lastDate}, color: ${color}`);
              } else {
                console.log(`Block ${namaBlok}: No inspection data, color: ${color}`);
              }
            }
          } else {
            // If block has no name, use red color
            feature.properties.color = "#e74c3c";
            colorStats.red++;
          }
        });

        // Log coloring statistics
        console.log("Block coloring statistics:", colorStats);

        // Apply the changes to the map
        geoSource.setData(geoData);
      }

      // Tambahkan proses untuk lkk-source
      const lkkSource = mapInstance.getSource("lkk-source") as maplibregl.GeoJSONSource;
      if (lkkSource) {
        const lkkData = (await lkkSource.getData()) as GeoJSON.FeatureCollection;
        lkkData.features.forEach((feature: any) => {
          const blockCodeRaw = feature.properties?.blok_code || feature.properties?.block_code;
          const blockCode6 = blockCodeRaw && blockCodeRaw.length === 8
            ? blockCodeRaw.slice(0, 6)
            : blockCodeRaw;

          let lastDate = latestInspections.data.find((item: any) =>
            blockCode6 && item.blok === blockCode6
          )?.latest_inspection;

          if (!lastDate && blockCode6) {
            const matchingBlock = latestInspections.data.find((item: any) =>
              item.blok.toLowerCase() === blockCode6.toLowerCase()
            );
            if (matchingBlock) lastDate = matchingBlock.latest_inspection;
          }

          const color = getBlockColor(lastDate ? new Date(lastDate) : undefined);
          feature.properties.color = color;
        });
        lkkSource.setData(lkkData);
      }

      // Tambahkan kembali marker-inspeksi dengan data baru (yang sudah difilter)
      const markerGeoJSON: GeoJSON.FeatureCollection<GeoJSON.Point, any> = {
        type: "FeatureCollection",
        features: inspections.map((ins) => {
          const categoryColor = getCategoryColor(ins.category);

          return {
            type: "Feature",
            geometry: { type: "Point", coordinates: [ins.longitude, ins.latitude] },
            properties: {
              uuid: ins.uuid,
              id: ins.id,
              estate: ins.estate,
              divisi: ins.divisi,
              category: ins.category,
              category_color: categoryColor,
              penalty_code: ins.penalty_code,
              penalty_description: ins.penalty_description,
              pemanen: ins.pemanen,
              emp_pemanen: ins.emp_pemanen,
              pemanen_nomor_induk_pegawai: ins.pemanen_nomor_induk_pegawai,
              pemanen_kemandoran: ins.pemanen_kemandoran,
              emp_pemanen_nomor_induk_pegawai: ins.emp_pemanen_nomor_induk_pegawai,
              emp_pemanen_kemandoran: ins.emp_pemanen_kemandoran,
              no_tph: ins.no_tph,
              no_baris: ins.no_baris,
              no_pokok: ins.no_pokok,
              qty: ins.qty,
              ripe_qty: ins.ripe_qty,
              unripe_qty: ins.unripe_qty,
              rotten_qty: ins.rotten_qty,
              lstalk_qty: ins.lstalk_qty,
              loose_qty: ins.loose_qty,
              tph: ins.tph,
              abnormal_01_qty: ins.abnormal_01_qty,
              abnormal_02_qty: ins.abnormal_02_qty,
              abnormal_03_qty: ins.abnormal_03_qty,
              abnormal_04_qty: ins.abnormal_04_qty,
              no_potong: ins.no_potong,
              user: ins.user,
              nomor_induk_pegawai: ins.nomor_induk_pegawai,
              job_position: ins.job_position,
              blok: ins.blok,
              tanggal: ins.tanggal,
              waktu: ins.waktu,
              hasil_inspeksi: ins.hasil_inspeksi,
              picture_urls: ins.pictures?.map((p: any) => `${API_BASE_URL}${p.server_uri}`) || []
            },
          };
        }),
      };
      setInspeksiData(markerGeoJSON);
      mapInstance.addSource("inspeksi-source", {
        type: "geojson",
        data: markerGeoJSON,
      });
      mapInstance.addLayer({
        id: "inspeksi-marker",
        type: "circle",
        source: "inspeksi-source",
        paint: {
          "circle-radius": 8,
          "circle-color": ["get", "category_color"],
          "circle-opacity": 0.8,
          "circle-stroke-width": 1,
          "circle-stroke-color": "#000",
        },
      });

      const lineFeatures: GeoJSON.Feature<GeoJSON.LineString>[] = [];
      const geojsonSource = mapInstance.getSource('geojson-source') as maplibregl.GeoJSONSource;
      const geojsonData = geojsonSource?._data as GeoJSON.FeatureCollection;

      if (!geojsonData) {
        console.error('GeoJSON data not loaded');
        return;
      }

      inspections.forEach((ins) => {
        // Skip if we don't have valid longitude/latitude
        if (!ins.longitude || !ins.latitude || isNaN(ins.longitude) || isNaN(ins.latitude)) {
          console.warn("Skipping inspection with invalid coordinates:", ins.id || "unknown");
          return;
        }

        const blockFeature = geojsonData.features.find((f: any) =>
          f.properties?.LandUse_26?.toLowerCase() === ins.blok?.toLowerCase()
        );

        if (blockFeature) {
          try {
            // Calculate block center - this is where NaN might occur
            const centerMass = turf.centerOfMass(blockFeature);

            // Validate center coordinates
            if (!centerMass?.geometry?.coordinates ||
              !Array.isArray(centerMass.geometry.coordinates) ||
              centerMass.geometry.coordinates.length !== 2 ||
              isNaN(centerMass.geometry.coordinates[0]) ||
              isNaN(centerMass.geometry.coordinates[1])) {
              console.warn(`Invalid block center for ${ins.blok}, using fallback calculation`);

              // Fallback to simpler calculation - average of all points in first ring
              const coords = (blockFeature.geometry as any)?.coordinates?.[0] || [];
              if (coords.length > 0) {
                let sumLng = 0, sumLat = 0;
                coords.forEach((coord: any) => {
                  sumLng += coord[0];
                  sumLat += coord[1];
                });
                const blockCenter = [sumLng / coords.length, sumLat / coords.length];

                const point = turf.point([ins.longitude, ins.latitude]);
                const isInside = turf.booleanPointInPolygon(point, blockFeature as any);

                if (!isInside) {
                  lineFeatures.push({
                    type: 'Feature',
                    geometry: {
                      type: 'LineString',
                      coordinates: [
                        [ins.longitude, ins.latitude],
                        blockCenter
                      ]
                    },
                    properties: {
                      category_color: getCategoryColor(ins.category),
                      source_point: { lng: ins.longitude, lat: ins.latitude },
                      block_center: blockCenter,
                      block_bbox: turf.bbox(blockFeature),
                      id: ins.id ?? Math.random().toString(36).substr(2, 9)
                    }
                  });
                }
              }
            } else {
              // Normal path - use centerOfMass result
              const blockCenter = centerMass.geometry.coordinates;
              const point = turf.point([ins.longitude, ins.latitude]);
              const isInside = turf.booleanPointInPolygon(point, blockFeature as any);

              if (!isInside) {
                lineFeatures.push({
                  type: 'Feature',
                  geometry: {
                    type: 'LineString',
                    coordinates: [
                      [ins.longitude, ins.latitude],
                      blockCenter
                    ]
                  },
                  properties: {
                    category_color: getCategoryColor(ins.category),
                    source_point: { lng: ins.longitude, lat: ins.latitude },
                    block_center: blockCenter,
                    block_bbox: turf.bbox(blockFeature),
                    id: ins.id ?? Math.random().toString(36).substr(2, 9)
                  }
                });
              }
            }
          } catch (err) {
            console.error(`Error processing line for inspection ${ins.id || "unknown"}:`, err);
          }
        }
      });
      const lkkData = lkkSource?._data as GeoJSON.FeatureCollection;

      if (lkkData) {
        inspections.forEach((ins) => {
          // Skip if we don't have valid longitude/latitude
          if (!ins.longitude || !ins.latitude || isNaN(ins.longitude) || isNaN(ins.latitude)) {
            return;
          }

          // Cari blok LKK berdasarkan blok inspeksi (6 digit)
          const blockFeature = lkkData.features.find((f: any) => {
            const rawCode = f.properties?.blok_code || f.properties?.block_code;
            const blockCode6 = rawCode && rawCode.length === 8 ? rawCode.slice(0, 6) : rawCode;
            return blockCode6 && ins.blok && blockCode6.toLowerCase() === ins.blok.toLowerCase();
          });

          if (blockFeature) {
            try {
              const centerMass = turf.centerOfMass(blockFeature);

              if (!centerMass?.geometry?.coordinates ||
                !Array.isArray(centerMass.geometry.coordinates) ||
                centerMass.geometry.coordinates.length !== 2 ||
                isNaN(centerMass.geometry.coordinates[0]) ||
                isNaN(centerMass.geometry.coordinates[1])) {

                const coords = (blockFeature.geometry as any)?.coordinates?.[0] || [];
                if (coords.length > 0) {
                  let sumLng = 0, sumLat = 0;
                  coords.forEach((coord: any) => {
                    sumLng += coord[0];
                    sumLat += coord[1];
                  });
                  const blockCenter = [sumLng / coords.length, sumLat / coords.length];

                  const point = turf.point([ins.longitude, ins.latitude]);
                  const isInside = turf.booleanPointInPolygon(point, blockFeature as any);

                  if (!isInside) {
                    lineFeatures.push({
                      type: 'Feature',
                      geometry: {
                        type: 'LineString',
                        coordinates: [
                          [ins.longitude, ins.latitude],
                          blockCenter
                        ]
                      },
                      properties: {
                        category_color: getCategoryColor(ins.category),
                        source_point: { lng: ins.longitude, lat: ins.latitude },
                        block_center: blockCenter,
                        block_bbox: turf.bbox(blockFeature),
                        id: ins.id ?? Math.random().toString(36).substr(2, 9)
                      }
                    });
                  }
                }
              } else {
                const blockCenter = centerMass.geometry.coordinates;
                const point = turf.point([ins.longitude, ins.latitude]);
                const isInside = turf.booleanPointInPolygon(point, blockFeature as any);

                if (!isInside) {
                  lineFeatures.push({
                    type: 'Feature',
                    geometry: {
                      type: 'LineString',
                      coordinates: [
                        [ins.longitude, ins.latitude],
                        blockCenter
                      ]
                    },
                    properties: {
                      category_color: getCategoryColor(ins.category),
                      source_point: { lng: ins.longitude, lat: ins.latitude },
                      block_center: blockCenter,
                      block_bbox: turf.bbox(blockFeature),
                      id: ins.id ?? Math.random().toString(36).substr(2, 9)
                    }
                  });
                }
              }
            } catch (err) {
              console.error(`Error processing line for LKK inspection ${ins.id || "unknown"}:`, err);
            }
          }
        });
      }

      console.log(`Validated ${lineFeatures.length} line features, adding to map...`);

      // remove old connection source/layer
      if (mapInstance.getLayer("connection-lines")) {
        mapInstance.removeLayer("connection-lines");
      }
      if (mapInstance.getSource("connection-lines-source")) {
        mapInstance.removeSource("connection-lines-source");
      }

      mapInstance.addSource("connection-lines-source", {
        type: "geojson",
        data: { type: "FeatureCollection", features: lineFeatures },
      });
      mapInstance.addLayer({
        id: "connection-lines",
        type: "line",
        source: "connection-lines-source",
        layout: { visibility: showConnectionLines ? "visible" : "none" },
        paint: { "line-color": ["get", "category_color"], "line-width": 2, "line-opacity": 0.7 },
      });

    } catch (error) {
      console.error("Error fetching inspeksi data:", error);
    }
  };


  // Callback yang dipanggil ketika tombol "Terapkan Filter" diklik
  const handleApplyFilters = useCallback(() => {
    setPendingApply(true);
  }, []);

  const focusToArea = () => {
    if (map) {
      map.flyTo({
        center: [104.083064, -3.125992],
        zoom: 10.9,
        essential: true,
      });
    }
  };

  // useEffect(() => {
  //     if (map) {
  //         fetchInspeksiData(map);
  //     }
  // }, [filters]);

  const zoomIn = () => {
    if (map) map.zoomIn();
  };

  const zoomOut = () => {
    if (map) map.zoomOut();
  };

  // Toggle layer satelit
  const toggleSatellite = () => {
    if (!map) return;
    const currentVisibility = map.getLayoutProperty("satellite-layer", "visibility");
    if (currentVisibility === "visible") {
      map.setLayoutProperty("satellite-layer", "visibility", "none");
      setIsSatellite(false);
    } else {
      map.setLayoutProperty("satellite-layer", "visibility", "visible");
      setIsSatellite(true);
    }
  };


  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const handleMarkerClick = useCallback((feature: any) => {
    if (!feature || !feature.properties) return;

    const props = feature.properties;
    let pics: string[] = [];

    try {
      // Handle pictures array - could be a string or an array
      const raw = props.picture_urls;
      pics = typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch (err) {
      console.warn("Failed to parse picture URLs:", err);
      pics = [];
    }

    setSelectedInspeksi({
      uuid: props.uuid,
      id: props.id,
      estate: props.estate,
      divisi: props.divisi,
      // Category-related fields
      category: props.category,
      penalty_code: props.penalty_code,
      penalty_description: props.penalty_description,
      // Harvest-related fields
      pemanen: props.pemanen,
      emp_pemanen: props.emp_pemanen,
      pemanen_nomor_induk_pegawai: props.pemanen_nomor_induk_pegawai,
      pemanen_kemandoran: props.pemanen_kemandoran,
      emp_pemanen_nomor_induk_pegawai: props.emp_pemanen_nomor_induk_pegawai,
      emp_pemanen_kemandoran: props.emp_pemanen_kemandoran,
      no_tph: props.no_tph,
      no_baris: props.no_baris,
      no_pokok: props.no_pokok,
      qty: props.qty,
      ripe_qty: props.ripe_qty,
      unripe_qty: props.unripe_qty,
      rotten_qty: props.rotten_qty,
      lstalk_qty: props.lstalk_qty,
      loose_qty: props.loose_qty,
      tph: props.tph,
      abnormal_01_qty: props.abnormal_01_qty,
      abnormal_02_qty: props.abnormal_02_qty,
      abnormal_03_qty: props.abnormal_03_qty,
      abnormal_04_qty: props.abnormal_04_qty,
      no_potong: props.no_potong,
      // User information
      user: props.user,
      nomor_induk_pegawai: props.nomor_induk_pegawai,
      job_position: props.job_position,
      // Location and time
      blok: props.blok,
      date: props.tanggal,
      time: props.waktu,
      // Description and pictures
      description: props.hasil_inspeksi,
      pictures: pics,
    });
  }, []);

  useEffect(() => {
    if (!map) return;

    console.log("Toggling connection lines visibility:", showConnectionLines);

    // Fungsi untuk mengubah visibilitas
    const updateVisibility = () => {
      try {
        if (map.getLayer('connection-lines')) {
          const visibility = showConnectionLines ? 'visible' : 'none';
          map.setLayoutProperty('connection-lines', 'visibility', visibility);
          console.log("Connection lines visibility set to:", visibility);
          return true;
        }
        return false;
      } catch (err) {
        console.error("Error setting connection line visibility:", err);
        return false;
      }
    };

    // Coba update jika map sudah siap
    if (map.isStyleLoaded()) {
      if (!updateVisibility() && showConnectionLines) {
        console.log("Connection lines layer not found, will retry later");
        // Layer belum ada, tunggu 1 detik dan coba lagi
        setTimeout(updateVisibility, 1000);
      }
    } else {
      // Tunggu map load
      map.once('load', updateVisibility);
    }
  }, [map, showConnectionLines]);

  return (
    <Box position="relative" h="100%" overflow="hidden">
      <Flex h="full">
        {/* Sidebar */}
        <Box
          position={{ base: 'fixed', md: 'fixed' }}
          top={{ base: '60px', md: '64px' }}
          left={{ base: sidebarOpen ? 0 : '-100%', md: sidebarOpen ? 0 : -350 }}
          width={{ base: 'calc(100% - 64px)', md: '350px' }}
          height={{ base: '100vh', md: 'calc(100vh - 64px)' }}
          bg="white"
          boxShadow="lg"
          zIndex={15}
          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        >
          {sidebarOpen && (
            <FilterInspeksi
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={clearFilters}
              onApplyFilters={handleApplyFilters}
            />
          )}
        </Box>

        {/* Tombol hide/unhide di luar sidebar */}
        <Box
          position="fixed"
          top={{ base: "48px", md: "34px" }}
          left={{
            base: sidebarOpen ? 'calc(100% - 56px)' : "10px",
            md: sidebarOpen ? "360px" : "10px"
          }}
          zIndex={200}
          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        >
          {sidebarOpen ? (
            <IconButton
              aria-label="Sembunyikan Sidebar"
              icon={<MdOutlineKeyboardArrowLeft />}
              size="lg"
              top={{ base: 5, md: 10 }}
              onClick={() => setSidebarOpen(false)}
              borderRadius="md"
              boxShadow="md"
              // bg="customGreen.500"
              color="white"
              _hover={{ bg: "customGreen.600" }}
            />
          ) : (
            <IconButton
              aria-label="Tampilkan Sidebar"
              icon={<MdOutlineKeyboardArrowRight />}
              size="lg"
              top={{ base: "20px", md: 10 }}
              onClick={() => setSidebarOpen(true)}
              borderRadius="md"
              boxShadow="md"
              bg="customGreen.500"
              color="white"
              _hover={{ bg: "customGreen.600" }}
            />
          )}
        </Box>
        {/* Map Container */}
        <Box
          flex={1}
          position="relative"
          ml={{ base: 0, md: sidebarOpen ? '350px' : 0 }}
        // transition="margin 0.3s"
        >
          <Box
            ref={mapContainerRef}
            id="map"
            h="full"
            w="full"
            sx={{
              ".maplibre-gl-canvas": {
                transition: "filter 0.3s",
              },
              "&:hover .maplibre-gl-canvas": {
                filter: "brightness(0.98)",
              },
            }}
          />
          {/* <MapMagnifier 
            map={map} 
            inspeksiData={inspeksiData} 
          /> */}
          <ClusterExploder
            map={map}
            inspeksiData={inspeksiData}
            onMarkerClick={handleMarkerClick}
          />

          {/* Map Controls */}
          <Flex
            position="absolute"
            bottom={{ base: "15%", sm: 6, md: 8 }} // lebih natural di berbagai ukuran layar
            right={{ base: 4, sm: 6, md: 8 }}
            direction="column"
            gap={2}
            zIndex={10}
          >
            <IconButton
              aria-label="Show Legend"
              icon={<MdOutlineLegendToggle />}
              onClick={() => setIsLegendOpen(true)}
              variant="solid"
              colorScheme="green"
              borderRadius="full"
              boxShadow="lg"
              w="40px"
              h="40px"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              mb={2}
            />
            <IconButton
              aria-label={showConnectionLines ? "Hide Connection Lines" : "Show Connection Lines"}
              icon={showConnectionLines ? <MdClose /> : <TbLine />}
              onClick={() => setShowConnectionLines(!showConnectionLines)}
              variant="solid"
              colorScheme="blue"
              borderRadius="full"
              boxShadow="lg"
              w="40px"
              h="40px"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            />
            <IconButton
              aria-label="Toggle Satelit"
              icon={<Text fontSize="x-small">{isSatellite ? "Satelit" : "Peta"}</Text>}
              onClick={toggleSatellite}
              variant="solid"
              colorScheme="blue"
              borderRadius="full"
              boxShadow="lg"
              w="40px"  // Lebar tetap
              h="40px"  // Tinggi tetap
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            />
            <IconButton
              aria-label="Focus area"
              icon={<MdMyLocation size={20} />}
              onClick={focusToArea}
              variant="solid"
              colorScheme="blue"
              borderRadius="full"
              boxShadow="lg"
              w="40px"
              h="40px"
            />
            <ButtonGroup
              variant="solid"
              colorScheme="blue"
              isAttached
              borderRadius="full"
              boxShadow="lg"
              orientation="vertical"
            >
              <IconButton
                aria-label="Zoom in"
                icon={<FiPlus size={20} />}
                onClick={zoomIn}
                borderBottomRadius="0"
                w="40px"
                h="40px"
              />
              <IconButton
                aria-label="Zoom out"
                icon={<FiMinus size={20} />}
                onClick={zoomOut}
                borderTopRadius="0"
                w="40px"
                h="40px"
              />
            </ButtonGroup>
          </Flex>
        </Box>
      </Flex>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedInspeksi}
        onClose={() => setSelectedInspeksi(null)}
        size={{ base: 'lg', md: 'lg' }}
        isCentered
      >
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent borderRadius="lg" overflow="hidden" id="modal-detail-inspeksi">
          <ModalHeader bg="customGold.500" borderBottomWidth="1px" borderColor="gray.100">
            {/* <Heading color={"white"} size="md">Detail Inspeksi #{selectedInspeksi?.id}</Heading> */}
            <Heading color={"white"} size="md">
              {selectedInspeksi?.uuid
                ? `Detail Inspeksi #${selectedInspeksi.uuid.split('-').pop()}`
                : `Detail Inspeksi #${selectedInspeksi?.id}`
              }
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4}>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <DetailItem label="Estate" value={selectedInspeksi?.estate} />
              <DetailItem label="Divisi" value={selectedInspeksi?.divisi} />
              <DetailItem label="Blok" value={selectedInspeksi?.tph ? `${selectedInspeksi.blok} (${selectedInspeksi.tph})` : selectedInspeksi?.blok} />
              {selectedInspeksi?.category &&
                <DetailItem label="Kategori" value={selectedInspeksi.category} />}
              {selectedInspeksi?.penalty_code &&
                <DetailItem label="Kode Penalti" value={selectedInspeksi.penalty_code} />}
              {(selectedInspeksi?.emp_pemanen || selectedInspeksi?.pemanen) && (
                <DetailItem
                  label="Pemanen"
                  value={
                    <Box>
                      <Text>
                        {(selectedInspeksi.emp_pemanen || selectedInspeksi.pemanen) + " - " +
                          (selectedInspeksi.emp_pemanen_nomor_induk_pegawai || selectedInspeksi.pemanen_nomor_induk_pegawai || "-")}
                        {selectedInspeksi.no_potong && ` - ${selectedInspeksi.no_potong}`}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {selectedInspeksi.emp_pemanen_kemandoran || selectedInspeksi.pemanen_kemandoran || ""}
                      </Text>
                    </Box>
                  }
                />
              )}
              <DetailItem label={selectedInspeksi?.job_position ?? "Jabatan"} value={`${selectedInspeksi?.user} - ${selectedInspeksi?.nomor_induk_pegawai}`} />
              <DetailItem label="Tanggal" value={`${selectedInspeksi?.date} ${selectedInspeksi?.time}`} />
              {selectedInspeksi?.no_tph &&
                <DetailItem label="No. TPH" value={selectedInspeksi.no_tph} />}
              {selectedInspeksi?.no_baris &&
                <DetailItem label="No. Baris" value={selectedInspeksi.no_baris} />}
              {selectedInspeksi?.no_pokok &&
                <DetailItem label="No. Pokok" value={selectedInspeksi.no_pokok} />}
              {selectedInspeksi?.qty !== undefined &&
                <DetailItem label="Jumlah" value={selectedInspeksi.qty.toString()} />}
              {selectedInspeksi?.penalty_description !== undefined &&
                <DetailItem label="Deskripsi Penalti" value={selectedInspeksi.penalty_description.toString()} />
              }
              {selectedInspeksi?.ripe_qty !== undefined &&
                <DetailItem label="Ripe Qty" value={selectedInspeksi.ripe_qty.toString()} />}
              {selectedInspeksi?.unripe_qty !== undefined &&
                <DetailItem label="Unripe Qty" value={selectedInspeksi.unripe_qty.toString()} />}
              {selectedInspeksi?.rotten_qty !== undefined &&
                <DetailItem label="Rotten Qty" value={selectedInspeksi.rotten_qty.toString()} />}
              {selectedInspeksi?.lstalk_qty !== undefined &&
                <DetailItem label="Lstalk Qty" value={selectedInspeksi.lstalk_qty.toString()} />}
              {selectedInspeksi?.loose_qty !== undefined &&
                <DetailItem label="Loose Qty" value={selectedInspeksi.loose_qty.toString()} />}
              {selectedInspeksi?.abnormal_01_qty !== undefined &&
                <DetailItem label="Abnormal 01 Qty" value={selectedInspeksi.abnormal_01_qty.toString()} />}
              {selectedInspeksi?.abnormal_02_qty !== undefined &&
                <DetailItem label="Abnormal 02 Qty" value={selectedInspeksi.abnormal_02_qty.toString()} />}
              {selectedInspeksi?.abnormal_03_qty !== undefined &&
                <DetailItem label="Abnormal 03 Qty" value={selectedInspeksi.abnormal_03_qty.toString()} />}
              {selectedInspeksi?.abnormal_04_qty !== undefined &&
                <DetailItem label="Abnormal 04 Qty" value={selectedInspeksi.abnormal_04_qty.toString()} />}
            </Grid>
            <Box mt={4}>
              <Text fontWeight="500" mb={2}>
                Hasil Inspeksi
              </Text>
              <Text fontSize="sm" color="gray.600" whiteSpace="pre-line">
                {selectedInspeksi?.description || "-"}
              </Text>
            </Box>
            {selectedInspeksi?.pictures && selectedInspeksi.pictures.length > 0 && (
              <Box mt={4}>
                <Text fontWeight="500" mb={2}>
                  Foto
                </Text>
                <Box overflowX="auto">
                  <HStack spacing={4}>
                    {selectedInspeksi.pictures.map((url, idx) => (
                      <Image
                        key={idx}
                        src={url}
                        alt={`Inspeksi ${idx + 1}`}
                        borderRadius="lg"
                        objectFit="cover"
                        maxH="120px"
                        cursor="pointer"
                        onClick={() => setPreviewImage(url)}
                        _hover={{ opacity: 0.8 }}
                      />
                    ))}
                  </HStack>
                </Box>
              </Box>
            )}
          </ModalBody>
          <Flex mb={6} justify="flex-end" gap={3}>
            {/* <Button size="sm" onClick={shareViaWhatsApp} colorScheme="whatsapp">WhatsApp</Button>
            <Button size="sm" onClick={shareViaEmail} colorScheme="blue">Email</Button>
            <Button size="sm" onClick={exportPDF} colorScheme="orange">Unduh PDF</Button> */}
            {/* <Button size="sm" onClick={copyLink} variant="outline">Salin Link</Button> */}
          </Flex>

        </ModalContent>
      </Modal>
      <Modal isOpen={!!previewImage} onClose={() => setPreviewImage(null)} isCentered size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={0}>
            <Image src={previewImage!} alt="Preview" width="100%" objectFit="contain" />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isLegendOpen} onClose={() => setIsLegendOpen(false)} size="md" isCentered>
        <ModalOverlay bg="blackAlpha.300" />
        <ModalContent borderRadius="2xl" overflow="hidden">
          <ModalHeader bg="customGold.500" borderBottomWidth="1px" borderColor="gray.100">
            <Heading color="white" size="md">Legend Warna Inspeksi</Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4}>
            <HStack spacing={12} align="start">
              <VStack spacing={4} align="start">
                <Text fontSize="lg" fontWeight="500" mb={2}>
                  Blok
                </Text>
                <LegendItem color={_03_TRANS_BLUE} description="Di Inspeksi Bulan Ini" />
                <LegendItem color={_02_TRANS_YELLOW} description="Inspeksi Terakhir Bulan Lalu" />
                <LegendItem color={_01_TRANS_RED} description=">2 Bulan Tidak Diinspeksi" />
              </VStack>

              <VStack spacing={4} align="start">
                <Text fontSize="lg" fontWeight="500" mb={2}>
                  Titik Inspeksi
                </Text>
                <LegendItem color={_PANEN} description="Panen, Mutu Ancak, Mutu Buah" />
                <LegendItem color={_RAWAT} description="Rawat" />
                <LegendItem color={_PUPUK} description="Pupuk" />
                <LegendItem color={_PATROLI} description="Patroli/Security" />
                <LegendItem color={_TRAKS} description="Traksi" />
                <LegendItem color={_INFRA} description="Infrastruktur & Sipil" />
                <LegendItem color={_HPT} description="HPT/R&D" />
              </VStack>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const DetailItem = ({ label, value }: { label: string; value?: string | React.ReactNode }) => (
  <Box>
    <Text fontSize="sm" color="gray.500" mb={1}>
      {label}
    </Text>
    {typeof value === 'string' ? (
      <Text fontWeight="500">{value || "-"}</Text>
    ) : (
      value || <Text fontWeight="500">-</Text>
    )}
  </Box>
);

const LegendItem = ({ color, description }: { color: string, description: string }) => (
  <HStack>
    <Box w="20px" h="20px" bg={color} borderRadius="md" border="1px solid gray" />
    <Text fontSize="sm" color="gray.700">{description}</Text>
  </HStack>
);

export default MapInspectionComponent;
